// React
import { useState } from 'react'

// Next
import { useRouter } from 'next/router'

// Components
import { Button } from '@/components/ui/button'
import { LoginButton } from '@/components/blocks/login-button'

// Utils
import { postData } from '@supermeme-ai/utils'
import { useSession, useSubscription } from '@/utils/auth/user-context'
import { getStripe } from '@/utils/clients/stripe/browser'

// External
import { Dialog } from '@headlessui/react'
import { toast } from 'react-hot-toast'
import { Check, Loader2, MoveUpIcon, RocketIcon, XIcon } from 'lucide-react'

// i18n
import { useTranslation } from 'next-i18next'

const priceIds = {
  soloMonthly: process.env.NEXT_PUBLIC_SOLO_MONTHLY_PRICE_ID,
  soloYearly: process.env.NEXT_PUBLIC_SOLO_YEARLY_PRICE_ID,
  startupMonthly: process.env.NEXT_PUBLIC_STARTUP_MONTHLY_PRICE_ID,
  startupYearly: process.env.NEXT_PUBLIC_STARTUP_YEARLY_PRICE_ID,
  enterpriseTier1Monthly: process.env.NEXT_PUBLIC_ENTERPRISE_MONTHLY_PRICE_ID,
  enterpriseTier1Yearly: process.env.NEXT_PUBLIC_ENTERPRISE_YEARLY_PRICE_ID,
  enterpriseTier2Monthly:
    process.env.NEXT_PUBLIC_ENTERPRISE_TIER_2_MONTHLY_PRICE_ID,
  enterpriseTier2Yearly:
    process.env.NEXT_PUBLIC_ENTERPRISE_TIER_2_YEARLY_PRICE_ID,
  enterpriseTier3Monthly:
    process.env.NEXT_PUBLIC_ENTERPRISE_TIER_3_MONTHLY_PRICE_ID,
  enterpriseTier3Yearly:
    process.env.NEXT_PUBLIC_ENTERPRISE_TIER_3_YEARLY_PRICE_ID,
}

const Pricing = () => {
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>(
    'year'
  )
  const [priceIdLoading, setPriceIdLoading] = useState('')
  const [showEnterprisePlans, setShowEnterprisePlans] = useState(false)
  const session = useSession()
  const subscription = useSubscription()
  const router = useRouter()
  const { t } = useTranslation('components/pricing')

  const soloLoading =
    priceIdLoading === priceIds.soloMonthly ||
    priceIdLoading === priceIds.soloYearly
  const startupLoading =
    priceIdLoading === priceIds.startupMonthly ||
    priceIdLoading === priceIds.startupYearly
  const tier1Loading =
    priceIdLoading === priceIds.enterpriseTier1Monthly ||
    priceIdLoading === priceIds.enterpriseTier1Yearly
  const tier2Loading =
    priceIdLoading === priceIds.enterpriseTier2Monthly ||
    priceIdLoading === priceIds.enterpriseTier2Yearly
  const tier3loading =
    priceIdLoading === priceIds.enterpriseTier3Monthly ||
    priceIdLoading === priceIds.enterpriseTier3Yearly

  const handleCheckout = async (priceId: string) => {
    setPriceIdLoading(priceId)
    try {
      const { data } = await postData<{ sessionId: string }>(
        '/api/subscription/get-checkout-session',
        { priceId },
        session.access_token
      )
      const stripe = await getStripe()
      stripe.redirectToCheckout({ sessionId: data.sessionId })
    } catch (error) {
      toast.error(t('errorMessage'))
    } finally {
      setPriceIdLoading('')
    }
  }

  const redirectToCustomerPortal = async (priceId: string) => {
    setPriceIdLoading(priceId)
    const {
      data: { url },
      error,
    } = await postData<{ url: string }>(
      '/api/subscription/create-stripe-portal-link',
      {},
      session.access_token
    )

    if (error) {
      // TODO: Add error handling
    }

    router.push(url)
    setPriceIdLoading('')
  }

  const getPricingIntervalButtonStyling = (
    interval: 'year' | 'month'
  ): string => {
    const base = 'flex rounded-lg m-1 text-sm py-2 px-8 text-black bg-white'
    if (billingInterval === interval) {
      return base.concat(
        ' border-2 border-indigo-600 font-bold hover:bg-indigo-100'
      )
    } else {
      return base.concat(' border-2 border-gray-400 font-medium hover:bg--100')
    }
  }

  const billingIntervalButtons = (): JSX.Element => (
    <div className='space-y-1'>
      <div className='relative flex self-center rounded-lg border border-white bg-gray-100 p-1'>
        <Button
          type='button'
          className={getPricingIntervalButtonStyling('month')}
          onClick={() => setBillingInterval('month')}
        >
          {billingInterval === 'month' && <Check className='mr-1 h-5 w-5' />}
          {t('billing.monthly')}
        </Button>
        <Button
          type='button'
          className={getPricingIntervalButtonStyling('year')}
          onClick={() => setBillingInterval('year')}
        >
          {billingInterval === 'year' && <Check className='mr-1 h-5 w-5' />}
          {t('billing.yearly')}
        </Button>
      </div>
      <div>
        <div className='grid w-full grid-cols-2'>
          <div></div>
          <div className='flex justify-center'>
            <MoveUpIcon className='h-9 w-9 rotate-12 font-bold text-gray-900' />
          </div>
        </div>
      </div>
      <p className='text-center font-extrabold tracking-tight'>
        {t('billing.yearlyDiscount')}
      </p>
    </div>
  )

  const renderActionButton = (
    priceId: string,
    isLoading: boolean,
    tier: string
  ) => {
    if (!session) {
      return (
        <LoginButton
          buttonText={t('buttons.login')}
          buttonIcon={<RocketIcon className='mr-2 h-4 w-4' />}
          className='w-full'
        />
      )
    }

    return (
      <Button
        type='button'
        variant={tier === 'startup' ? 'default' : 'outline'}
        className={`w-full ${tier === 'startup' ? 'text-lg' : 'text-base'}`}
        size={tier === 'startup' ? 'lg' : 'sm'}
        disabled={isLoading}
        onClick={() => {
          if (subscription) {
            redirectToCustomerPortal(priceId)
          } else {
            handleCheckout(priceId)
          }
        }}
      >
        {isLoading ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : ''}
        {subscription ? t('buttons.manageAccount') : t('buttons.subscribe')}
      </Button>
    )
  }

  return (
    <>
      <div className='space-y-12'>
        <div className='flex flex-col items-center space-y-6'>
          <h1 className='text-center text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl'>
            <span className='block whitespace-nowrap sm:inline'>
              {t('header.title.part1')}
            </span>{' '}
            <span className='block whitespace-nowrap sm:inline'>
              {t('header.title.part2')}
            </span>
          </h1>
          <p className='text-center text-lg font-medium text-gray-800 sm:text-xl'>
            <span className='block whitespace-nowrap md:inline'>
              {t('header.subtitle.part1')}
            </span>{' '}
            <span className='block whitespace-nowrap md:inline'>
              {t('header.subtitle.part2')}
            </span>{' '}
            <span className='block whitespace-nowrap md:inline'>
              {t('header.subtitle.part3')}
            </span>
          </p>

          {billingIntervalButtons()}
        </div>

        <div className='bg-white pb-12 lg:pb-20'>
          <div className='relative z-0'>
            <div className='mx-auto max-w-7xl space-y-4 px-2 md:px-8'>
              <div className='mx-auto max-w-7xl px-2 md:px-8'>
                <div className='mx-auto grid max-w-md grid-cols-1 gap-8 lg:max-w-4xl lg:grid-cols-2'>
                  <div className='flex flex-col justify-between rounded-2xl bg-white p-8 ring-1 ring-gray-900/30 sm:p-10'>
                    <div>
                      <h3 className='text-3xl font-semibold text-gray-900'>
                        {t('plans.solo.title')}{' '}
                        {billingInterval === 'year' && (
                          <span className='text-xl'>
                            {t('plans.solo.saveText')}
                          </span>
                        )}
                      </h3>
                      <div className='mt-4 flex items-baseline gap-x-2'>
                        <span className='text-5xl font-bold tracking-tight text-gray-900'>
                          $
                        </span>
                        <span className='text-5xl font-bold tracking-tight text-gray-900'>
                          {billingInterval === 'month'
                            ? t('plans.solo.price.monthly')
                            : t('plans.solo.price.yearly')}
                        </span>
                        <span className='text-base font-semibold leading-7 text-gray-600'>
                          {t('pricing.perMonth')}
                        </span>
                      </div>
                      {billingInterval === 'year' && (
                        <p className='mt-2 text-sm'>
                          {t('pricing.billedYearly')}
                        </p>
                      )}
                      <p className='mt-6 text-base leading-7 text-gray-600'>
                        {t('plans.solo.description')}
                      </p>
                      <ul
                        role='list'
                        className='mt-10 space-y-4 text-sm leading-6 text-gray-600'
                      >
                        {(
                          t('features.solo', {
                            returnObjects: true,
                          }) as string[]
                        ).map((feature) => (
                          <li key={feature} className='flex gap-x-3'>
                            <Check
                              className='h-6 w-6 flex-shrink-0 text-green-500'
                              aria-hidden='true'
                            />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className='mt-8'>
                      {renderActionButton(
                        billingInterval === 'month'
                          ? priceIds.soloMonthly
                          : priceIds.soloYearly,
                        soloLoading,
                        'solo'
                      )}
                    </div>
                  </div>

                  <div className='relative flex flex-col justify-between rounded-2xl bg-white p-8 ring-2 ring-indigo-600 sm:p-10'>
                    <div className='absolute inset-x-0 top-0 translate-y-px transform'>
                      <div className='flex -translate-y-1/2 transform justify-center'>
                        <span className='inline-flex rounded-full bg-indigo-600 px-4 py-1 text-sm font-semibold text-white'>
                          {t('plans.startup.popularBadge')}
                        </span>
                      </div>
                    </div>
                    <div>
                      <h3 className='text-3xl font-semibold text-gray-900'>
                        {t('plans.startup.title')}{' '}
                        {billingInterval === 'year' && (
                          <span className='text-xl'>
                            {t('plans.startup.saveText')}
                          </span>
                        )}
                      </h3>
                      <div className='mt-4 flex items-baseline gap-x-2'>
                        <span className='text-5xl font-bold tracking-tight text-gray-900'>
                          {billingInterval === 'month'
                            ? t('plans.startup.price.monthly')
                            : t('plans.startup.price.yearly')}
                        </span>
                        <span className='text-base font-semibold leading-7 text-gray-600'>
                          {t('pricing.perMonth')}
                        </span>
                      </div>
                      {billingInterval === 'year' && (
                        <p className='mt-2 text-sm'>
                          {t('pricing.billedYearly')}
                        </p>
                      )}
                      <p className='mt-6 text-base leading-7 text-gray-600'>
                        {t('plans.startup.description')}
                      </p>
                      <ul
                        role='list'
                        className='mt-10 space-y-4 text-sm leading-6 text-gray-600'
                      >
                        {(
                          t('features.startup', {
                            returnObjects: true,
                          }) as string[]
                        ).map((feature) => (
                          <li key={feature} className='flex gap-x-3'>
                            <Check
                              className='h-6 w-6 flex-shrink-0 text-green-500'
                              aria-hidden='true'
                            />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className='mt-8'>
                      {renderActionButton(
                        billingInterval === 'month'
                          ? priceIds.startupMonthly
                          : priceIds.startupYearly,
                        startupLoading,
                        'startup'
                      )}
                    </div>
                  </div>

                  <div className='flex flex-col items-start gap-x-8 gap-y-6 rounded-2xl p-8 ring-1 ring-gray-900/30 sm:gap-y-10 sm:p-10 lg:col-span-2 lg:flex-row lg:items-center'>
                    <div className='lg:min-w-0 lg:flex-1'>
                      <h3 className='text-3xl font-semibold text-gray-900'>
                        {t('plans.enterprise.title')}
                      </h3>
                      <p className='mt-1 text-lg leading-7 text-gray-600'>
                        {t('plans.enterprise.descriptionPart1')}{' '}
                        <b>{t('plans.enterprise.descriptionPart2')}</b>
                        {t('plans.enterprise.descriptionPart3')}
                      </p>
                    </div>
                    <Button
                      type='button'
                      variant='outline'
                      className='text-base'
                      onClick={() => setShowEnterprisePlans(true)}
                    >
                      {t('buttons.viewEnterprisePlans')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enterprise plans dialog */}
      <Dialog
        open={showEnterprisePlans}
        onClose={() => setShowEnterprisePlans(false)}
        className='relative z-50'
      >
        {/* The backdrop, rendered as a fixed sibling to the panel container */}
        <div className='fixed inset-0 bg-black/30' aria-hidden='true' />

        {/* Full-screen scrollable container */}
        <div className='fixed inset-0 overflow-y-auto'>
          {/* Container to center the panel */}
          <div className='flex min-h-full items-center justify-center p-4'>
            {/* The actual dialog panel  */}
            <Dialog.Panel className='relative mx-auto max-w-7xl space-y-8 rounded bg-white py-8'>
              <button
                className='absolute right-4 top-4'
                onClick={() => setShowEnterprisePlans(false)}
              >
                <XIcon className='h-6 w-6 text-gray-700' />
              </button>

              <Dialog.Title className='text-center text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl'>
                {t('plans.enterprise.dialogTitle')}
              </Dialog.Title>

              <div className='flex flex-col items-center'>
                {billingIntervalButtons()}
              </div>

              <div className='bg-white'>
                <div className='relative z-0'>
                  <div className='mx-auto max-w-7xl space-y-4 px-2 md:px-8'>
                    <div className='relative lg:grid lg:grid-cols-7'>
                      <div className='mx-auto max-w-md lg:col-start-1 lg:col-end-3 lg:row-start-2 lg:row-end-3 lg:mx-0 lg:max-w-none'>
                        <div className='flex h-full flex-col overflow-hidden rounded-lg shadow-lg lg:rounded-none lg:rounded-l-lg'>
                          <div className='flex flex-1 flex-col'>
                            <div className='bg-white px-6 py-10'>
                              <div>
                                <h3
                                  className='text-center text-2xl font-medium text-gray-900'
                                  id='tier-hobby'
                                >
                                  {t('plans.enterprise.tier1.title')}
                                </h3>
                                <div className='mt-4 flex items-center justify-center'>
                                  <span className='flex items-start px-2 text-4xl tracking-tight text-gray-900'>
                                    <span className='mr-1 mt-2 text-2xl font-medium tracking-tight'>
                                      $
                                    </span>
                                    <span className='font-bold'>
                                      {billingInterval === 'month'
                                        ? t(
                                            'plans.enterprise.tier1.price.monthly'
                                          )
                                        : t(
                                            'plans.enterprise.tier1.price.yearly'
                                          )}
                                    </span>
                                  </span>
                                  <span className='text-lg font-medium text-gray-500'>
                                    {t('pricing.perMonth')}
                                  </span>
                                </div>
                                {billingInterval === 'year' && (
                                  <p className='text-center'>
                                    {t('pricing.billedYearly')}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className='flex flex-1 flex-col justify-between border-t-2 border-gray-100 bg-gray-50 p-6 sm:p-10 lg:p-6 xl:p-10'>
                              <ul role='list' className='space-y-4'>
                                {(
                                  t('features.enterprise.tier1', {
                                    returnObjects: true,
                                  }) as string[]
                                ).map((feature) => (
                                  <li
                                    key={feature}
                                    className='flex items-start'
                                  >
                                    <div className='flex-shrink-0'>
                                      <Check
                                        className='h-6 w-6 flex-shrink-0 text-green-500'
                                        aria-hidden='true'
                                      />
                                    </div>
                                    <p className='ml-3 text-base font-medium text-gray-500'>
                                      {feature}
                                    </p>
                                  </li>
                                ))}
                              </ul>
                              <div className='mt-8'>
                                {renderActionButton(
                                  billingInterval === 'month'
                                    ? priceIds.enterpriseTier1Monthly
                                    : priceIds.enterpriseTier1Yearly,
                                  tier1Loading,
                                  'enterprise1'
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className='mx-auto mt-10 max-w-lg lg:col-start-3 lg:col-end-6 lg:row-start-1 lg:row-end-4 lg:mx-0 lg:mt-0 lg:max-w-none'>
                        <div className='relative z-10 rounded-lg shadow-xl'>
                          <div
                            className='pointer-events-none absolute inset-0 rounded-lg border-2 border-indigo-600'
                            aria-hidden='true'
                          />
                          <div className='absolute inset-x-0 top-0 translate-y-px transform'>
                            <div className='flex -translate-y-1/2 transform justify-center'>
                              <span className='inline-flex rounded-full bg-indigo-600 px-4 py-1 text-base font-semibold text-white'>
                                {t('plans.startup.popularBadge')}
                              </span>
                            </div>
                          </div>
                          <div className='rounded-t-lg bg-white px-6 pb-10 pt-12'>
                            <div>
                              <h3
                                className='text-center text-3xl font-semibold tracking-tight text-gray-900 sm:-mx-6'
                                id='tier-growth'
                              >
                                {t('plans.enterprise.tier2.title')}
                              </h3>
                              <div className='mt-4 flex items-center justify-center'>
                                <span className='flex items-start px-2 text-4xl tracking-tight text-gray-900 sm:text-4xl'>
                                  <span className='mr-1 mt-2 text-2xl font-medium tracking-tight'>
                                    $
                                  </span>
                                  <span className='font-bold'>
                                    {billingInterval === 'month'
                                      ? t(
                                          'plans.enterprise.tier2.price.monthly'
                                        )
                                      : t(
                                          'plans.enterprise.tier2.price.yearly'
                                        )}
                                  </span>
                                </span>
                                <span className='text-xl font-medium text-gray-500'>
                                  {t('pricing.perMonth')}
                                </span>
                              </div>
                              {billingInterval === 'year' && (
                                <p className='text-center'>
                                  {t('pricing.billedYearly')}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className='rounded-b-lg border-t-2 border-gray-100 bg-gray-50 px-6 pb-8 pt-10 sm:px-10 sm:py-10'>
                            <ul role='list' className='space-y-4'>
                              {(
                                t('features.enterprise.tier2', {
                                  returnObjects: true,
                                }) as string[]
                              ).map((feature) => (
                                <li key={feature} className='flex items-start'>
                                  <div className='flex-shrink-0'>
                                    <Check
                                      className='h-6 w-6 flex-shrink-0 text-green-500'
                                      aria-hidden='true'
                                    />
                                  </div>
                                  <p className='ml-3 text-base font-medium text-gray-500'>
                                    {feature}
                                  </p>
                                </li>
                              ))}
                            </ul>
                            <div className='mt-10'>
                              {renderActionButton(
                                billingInterval === 'month'
                                  ? priceIds.enterpriseTier2Monthly
                                  : priceIds.enterpriseTier2Yearly,
                                tier2Loading,
                                'enterprise2'
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className='mx-auto mt-10 max-w-md lg:col-start-6 lg:col-end-8 lg:row-start-2 lg:row-end-3 lg:m-0 lg:max-w-none'>
                        <div className='flex h-full flex-col overflow-hidden rounded-lg shadow-lg lg:rounded-none lg:rounded-r-lg'>
                          <div className='flex flex-1 flex-col'>
                            <div className='bg-white px-6 py-10'>
                              <div>
                                <h3
                                  className='text-center text-2xl font-medium text-gray-900'
                                  id='tier-scale'
                                >
                                  {t('plans.enterprise.tier3.title')}
                                </h3>
                                <div className='mt-4 flex items-center justify-center'>
                                  <span className='flex items-start px-2 text-4xl tracking-tight text-gray-900'>
                                    <span className='mr-1 mt-2 text-2xl font-medium tracking-tight'>
                                      $
                                    </span>
                                    <span className='font-bold'>
                                      {billingInterval === 'month'
                                        ? t(
                                            'plans.enterprise.tier3.price.monthly'
                                          )
                                        : t(
                                            'plans.enterprise.tier3.price.yearly'
                                          )}
                                    </span>
                                  </span>
                                  <span className='text-lg font-medium text-gray-500'>
                                    {t('pricing.perMonth')}
                                  </span>
                                </div>
                                {billingInterval === 'year' && (
                                  <p className='text-center'>
                                    {t('pricing.billedYearly')}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className='flex flex-1 flex-col justify-between border-t-2 border-gray-100 bg-gray-50 p-6 sm:p-10 lg:p-6 xl:p-10'>
                              <ul role='list' className='space-y-4'>
                                {(
                                  t('features.enterprise.tier3', {
                                    returnObjects: true,
                                  }) as string[]
                                ).map((feature) => (
                                  <li
                                    key={feature}
                                    className='flex items-start'
                                  >
                                    <div className='flex-shrink-0'>
                                      <Check
                                        className='h-6 w-6 flex-shrink-0 text-green-500'
                                        aria-hidden='true'
                                      />
                                    </div>
                                    <p className='ml-3 text-base font-medium text-gray-500'>
                                      {feature}
                                    </p>
                                  </li>
                                ))}
                              </ul>
                              <div className='mt-8'>
                                {renderActionButton(
                                  billingInterval === 'month'
                                    ? priceIds.enterpriseTier3Monthly
                                    : priceIds.enterpriseTier3Yearly,
                                  tier3loading,
                                  'enterprise3'
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='flex items-center justify-center'>
                      <span>{t('comingSoon')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    </>
  )
}

export { Pricing }
